import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { IconNames, Task, TerminologyOptions } from "@/types/v2";
import { useStore } from "@/stores/useStore";
import { useTasks } from "@/hooks/store/useTasks";
import { useTaskPermissions } from "@/hooks/useTaskPermissions";
import { useTaskListActions } from "@/hooks/useTaskListActions";
import { filterTasks, TaskListFilters } from "@/lib/taskSelectors";
import { taskToRowProps } from "@/lib/taskRowProps";
import { TaskRow } from "@/components/Tasks/TaskRow";
import { TaskFormDialog } from "@/components/Tasks/TaskFormDialog";
import { TaskListDialogs } from "@/components/Tasks/TaskListDialogs";
import { Button } from "@/components/Base/Button";
import { Plus } from "lucide-react";
import { Icon } from "@/components/Common/Icon.tsx";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer.ts";

export interface RoTasksSectionProps {
  /** Firestore id of the selected card (RO). Tasks are scoped to it and new
   *  tasks pre-fill it. */
  cardId: string;
  /**
   * Overrides RO_STRIP_FILTERS.showCompleted. Left undefined, the RO details
   * side panel's default (hide completed tasks, except ones completed this
   * session) is preserved exactly. An on-demand popover (e.g. the card-footer
   * task icon) that the user just opened specifically to check task state
   * should pass `true` so a fully-complete list doesn't render empty.
   */
  showCompleted?: boolean;
  /** Merged onto the root <section> (e.g. to drop the box chrome when it is
   *  already inside a bordered popover). */
  className?: string;
}

// Hide already-completed tasks by default; tasks completed in this session
// stay visibly struck through (via the actions hook's session set). No filter
// bar or pagination — an RO has only a handful of tasks.
const RO_STRIP_FILTERS: TaskListFilters = {
  statuses: [],
  assigneeIds: [],
  showCompleted: false,
};

/** Compact, RO-scoped task list for the RO details side panel. */
export const RoTasksSection = ({
  cardId,
  showCompleted,
  className,
}: RoTasksSectionProps) => {
  const { tasksForCard } = useTasks();
  const { terminolize } = useTerminologyReplacer();
  const employees = useStore((s) => s.employees);
  const permissions = useTaskPermissions();
  const actions = useTaskListActions();

  const filters = useMemo<TaskListFilters>(
    () => ({
      ...RO_STRIP_FILTERS,
      showCompleted: showCompleted ?? RO_STRIP_FILTERS.showCompleted,
    }),
    [showCompleted]
  );

  // tasksForCard(cardId) preserves the store's prepareTasks order — filter
  // only, never sort. It returns a fresh array each call, so compute it inside
  // the memo and key off the stable inputs (tasksForCard is memoized on `tasks`,
  // so it changes exactly when the task list does). The 3rd arg (`now`) is
  // unused here: `filters.statuses` is always empty, so filterTasks never
  // consults it.
  const visible = useMemo(
    () =>
      filterTasks(
        tasksForCard(cardId),
        filters,
        undefined,
        actions.sessionCompletedIds
      ),
    [tasksForCard, cardId, filters, actions.sessionCompletedIds]
  );

  const addButton = (
    <Button className="size-8 p-0" aria-label="New task" title="New task">
      <Plus className="h-4 w-4" />
    </Button>
  );

  return (
    <section
      aria-label="Tasks"
      className={cn(
        "flex flex-col gap-2 rounded-md border border-border bg-popover p-4",
        className
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-bold leading-none text-foreground">
          Tasks ({visible.length})
        </h3>
        <TaskFormDialog trigger={addButton} initialRelatedRO={cardId} />
      </div>
      {visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-4 flex items-center justify-center">
            <Icon
              name={IconNames.check}
              size={24}
              className="text-muted-foreground"
            />
          </div>
          <h3 className="mb-2 text-lg font-medium text-muted-foreground">
            No Tasks Yet
          </h3>
          <p className="max-w-sm text-sm text-muted-foreground">
            Tasks you add to this{" "}
            {terminolize(
              "repair order",
              TerminologyOptions.repairOrder,
              "singular"
            )}{" "}
            will appear here.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {visible.map((task: Task) => (
            <TaskRow
              key={task.id}
              task={task}
              {...taskToRowProps(task, employees ?? [], permissions, undefined)}
              {...actions.rowHandlers}
            />
          ))}
        </div>
      )}
      <TaskListDialogs
        editingTask={actions.editingTask}
        setEditingTask={actions.setEditingTask}
        taskToDelete={actions.taskToDelete}
        setTaskToDelete={actions.setTaskToDelete}
        confirmDelete={actions.confirmDelete}
      />
    </section>
  );
};
